import React, { PureComponent } from "react";
import AudioPlayerComposition from "../compositions/AudioPlayerComposition";
import { createGlobalStyle } from "styled-components";

import Sponsor from "../components/Sponsor";
import { getUrlVars } from "../utils/Url";

export interface Props {
  isCustomPlaylist?: boolean;
  podcast: any;
  customColors?: any;
  maxVisibleItems?: any;
  showSponsor?: any;
  hidePodcastTitle?: any;
  hideEpisodeTitle?: any;
}

/**
 * Home page component - is responsible for rendering and passing podcast data
 * to the audioplayer- and sponsor component. Because cross browser compatibility
 * it also sets a browser version class to the homepage for additional styling.
 * The method 'addThemeClassToHTMLElement' adds a class based on a theme url
 * parameter. This should be dark or light.
 */
export default class HomePage extends PureComponent<Props, any> {
  constructor(props: Props) {
    super(props);

    this.state = {
      colors: this.props.customColors,
      maxVisibleItems: this.props.maxVisibleItems,
      showSponsor: this.props.showSponsor
    };

    this.addThemeClassToHTMLElement();
    this.setBrowserNameAsHtmlClass();
  }

  /**
   * When the user specifies a theme in de url parameters
   * it will be set as a class to the html element. For now
   * there is a dark and light theme. The light theme is enabled
   * by default and renders black text in the (sponsor) footer.
   *
   * @returns {any}
   */
  addThemeClassToHTMLElement() {
    const root = document.documentElement;
    const theme = getUrlVars().theme;
    if (theme) {
      root.classList.add(theme);
    } else if (
      document.querySelector(".audioWidget").getAttribute("data-theme")
    ) {
      root.classList.add(
        document.querySelector(".audioWidget").getAttribute("data-theme")
      );
    }
    return theme;
  }

  /**
   * Sets classes to the html to apply custom css for the difference in browsers.
   * Example: scrollbars are not visible on macOS/Mobile devices but they are
   * in windows browsers.
   */
  setBrowserNameAsHtmlClass() {
    const root = document.documentElement;
    const userAgent = navigator.userAgent || navigator.vendor;

    if (/android/i.test(userAgent)) {
      root.classList.add("android");
    }

    if (/MSIE 10/i.test(userAgent)) {
      root.classList.add("IE10");
    }

    if (/rv:11.0/i.test(userAgent)) {
      root.classList.add("IE11");
    }

    if (/^((?!chrome|android).)*safari/i.test(userAgent)) {
      root.classList.add("safari");
    }

    if (navigator.platform.match(/(Mac|iPhone|iPod|iPad)/i)) {
      root.classList.add("macLike");
    }
  }

  /**
   * If it's a podcast series we use this.props.podcast.title. The individual podcasts will
   * provide their own title. However, if it's a single podcast these titles will be the same and instead
   * of this.props.podcast.title we have to use this.props.podcast.programTitle (which is not
   * present in a podcastseries)
   */
  getPodcastTitle() {
    return this.props.podcast.programTitle
      ? this.props.podcast.programTitle
      : this.props.podcast.title;
  }

  render() {
    return (
      <div>
        <GlobalStyle />
        <AudioPlayerComposition
          isCustomPlaylist={this.props.isCustomPlaylist}
          websiteUrl={this.props.podcast.websiteUrl}
          branded={this.props.podcast.brandedPodcast}
          podcastTitle={this.getPodcastTitle()}
          episodes={this.props.podcast.episodes}
          artworkUrl={this.props.podcast.artworkUrl}
          sponsors={
            this.props.podcast.sponsors ? this.props.podcast.sponsors : []
          }
          maxVisibleItems={this.state.maxVisibleItems}
          programType={this.props.podcast.programType}
          customColors={this.state.colors ? this.state.colors : []}
          hidePodcastTitle={this.props.hidePodcastTitle}
          hideEpisodeTitle={this.props.hideEpisodeTitle}
        />

        {this.state.showSponsor === "false" ? null : (
          <Sponsor
            isCustomPlaylist={this.props.isCustomPlaylist}
            sponsorsLength={
              this.props.podcast.sponsors
                ? this.props.podcast.sponsors.length
                : 0
            }
            sponsors={
              this.props.podcast.sponsors ? this.props.podcast.sponsors : []
            }
          />
        )}
      </div>
    );
  }
}

const GlobalStyle = createGlobalStyle`
  * { user-select: none; }
  html {
      background-color: transparent;
  }

  body {
      margin: 0;
      font-family: sans-serif;
      font-family: GlacialBold,Arial,Helvetica,sans-serif;
  }

  img {
      width: 100%;
      outline: none;
      border: none;
  }

  a {
      text-decoration: none;
  }

  h1, h2 {
      font-weight: normal;
  }

  h1 {
      font-weight: bold;
  }

  html.macLike, html.safari, html.android {
      width: 1px;
      min-width: 100%; /*IOS Safari overflow fix*/
  }

  html.macLike, html.safari {
      .audio-player .player .indicators {
          opacity: 0;
      }
  }

  html.macLike.playback-enabled, html.safari.playback-enabled {
  .audio-player .player {
      .indicators {
          opacity: 1;
          transition: opacity 0.2s ease-in-out;
          }
      }
  }

  html.IE11, html.IE10 {
    #root {
      max-width: 100%;
      width: 100%;
      -ms-overflow-style: none;
      .audio-player {
        -ms-overflow-style: none;
        max-width: 100%;
        width: 100%;
        .playlist-wrapper {
          width: 100%;
          max-width: 100%;
          -ms-overflow-style: none;
          .playlist-item {
            -ms-overflow-style: none;
            position: relative;
            > span {
              position: absolute;
              &.playlist-item-icon {
                width: 25px;
                left: 0;
              }
              &.playlist-item-title {
                left: 25px;
                right: 70px;
              }
              &.playlist-item-duration {
                width: 55px;
                right: 15px;
              }
            }
          }
        }
      }
    }
  }

  html.IE10 {
    #root {
      max-width: 100%;
      width: 100%;
      -ms-overflow-style: none;
      .audio-player {
        > div {
          width: 100%;
          max-width: 100%;
        }
        .player {
          max-width: 100%;
          position: relative;
          width: 100%;
          display: block;
          >.metadata {
            position: absolute;
            top: 0px;
            left: 64px;
            width: calc(100% - 80px);
            height: 60px;
          }
        }
      }
    }
  }

  html.macLike, html.safari, html.android {
    .playlist-wrapper {
      .playlist {
        width: 100%;
        .playlist-item {
          width: 100%;
          .playlist-item-duration {
            text-align: right;
          }
        }
      }
    }
  }

.bnr {
  .progress {
    .time-line:before {
      background-color: rgba(255,255,255,0.1)
    }
  }
}
`;
